/* src/render/placeLabels.ts — town names over the flat far view.
 *
 * Once the camera is high enough that towns are smudges, their names appear — the nearest
 * dozen-and-a-bit, scaled with altitude, floating above the ground. Data is Natural Earth's
 * populated places (via the context worker); the pool keeps the draw-call cost at the pool
 * size regardless of how many places the dataset holds.
 */

import type { Object3D } from 'three';
import { toLocal, type Origin } from '../map/originRebase.ts';
import type { LabelPoint } from '../workers/protocol.ts';
import { LabelPool, type LabelItem } from './labels.ts';

/** Labels appear above this camera distance; below it the world speaks for itself. */
const MIN_DISTANCE = 3200;
const POOL = 14;

export interface PlaceLabels {
  update: (
    places: readonly LabelPoint[],
    origin: Origin,
    targetX: number,
    targetZ: number,
    distance: number,
  ) => void;
  hide: () => void;
  dispose: () => void;
}

export function createPlaceLabels(scene: Object3D): PlaceLabels {
  const pool = new LabelPool(scene, POOL);
  let countdown = 0;

  return {
    update(places, origin, targetX, targetZ, distance): void {
      if (distance < MIN_DISTANCE || places.length === 0) {
        pool.hide();
        countdown = 0;
        return;
      }
      // Re-picking nearest names every ~20 frames is plenty; positions are static.
      countdown -= 1;
      if (countdown > 0) return;
      countdown = 20;

      const reach = Math.min(40000, distance * 3.2);
      const scored: Array<{ p: LabelPoint; x: number; z: number; score: number }> = [];
      for (const p of places) {
        const [x, z] = toLocal(p.lon, p.lat, origin);
        const dist = Math.hypot(x - targetX, z - targetZ);
        if (dist > reach) continue;
        // Importance discounts distance: a capital's name survives further than a hamlet's.
        const rank = p.r ?? 8;
        scored.push({ p, x, z, score: dist * (1 + rank * 0.35) });
      }
      scored.sort((a, b) => a.score - b.score);

      const size = Math.min(900, Math.max(170, distance * 0.055));
      const items: LabelItem[] = scored.slice(0, POOL).map((s) => ({
        text: s.p.n,
        x: s.x,
        y: 30 + distance * 0.02,
        z: s.z,
        size,
      }));
      pool.show(items);
    },

    hide(): void {
      pool.hide();
      countdown = 0;
    },

    dispose(): void {
      pool.dispose();
    },
  };
}
